/**
 * Agency Service
 *
 * API service for agency/organization management:
 * - Workspace management
 * - Team management
 * - Agency settings
 * - God Mode (workspace impersonation)
 */

import apiClient, { formatApiError } from './api';
import { broadcastAuthChanged } from './authSync';
import { resetTracking } from './tracking';
import {
  Workspace,
  TeamMember,
  AgencySettings,
  InviteTeamMemberRequest,
  GodModeResponse,
  ClientAccessInfo,
  WorkspaceSearchResult,
} from '@/types/agency';

/**
 * Get all workspaces under the agency
 */
export const getWorkspaces = async (): Promise<Workspace[]> => {
  const response = await apiClient.get('/api/agency/workspaces');
  const workspaces = response.data.data;

  return workspaces.map((ws: Record<string, unknown>) => {
    const expiresAt = ws.expiresAt as string | null;
    const isActive = ws.isActive as boolean;

    // Calculate status based on isActive and expiration
    let status: 'active' | 'inactive' | 'expiring_soon' = 'active';

    if (!isActive) {
      status = 'inactive';
    } else if (expiresAt) {
      const expiryDate = new Date(expiresAt);
      const now = new Date();
      const daysUntilExpiry = Math.floor(
        (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysUntilExpiry < 0) {
        status = 'inactive';
      } else if (daysUntilExpiry <= 30) {
        status = 'expiring_soon';
      }
    }

    return {
      id: ws.id as number,
      name: ws.name as string,
      slug: ws.slug as string,
      logo: ws.logo as string | undefined,
      status,
      isActive,
      // Stats from API
      memberCount: (ws.memberCount as number) || 0,
      postCount: (ws.postCount as number) || 0,
      channelCount: (ws.channelCount as number) || 0,
      calendarCount: (ws.calendarCount as number) || 0,
      // Settings
      carteBlanche: (ws.carteBlanche as boolean) || false,
      // Dates
      expiresAt: expiresAt || undefined,
      lastActivityAt: ws.lastActivityAt as string | undefined,
      createdAt: ws.createdAt as string,
      // Customization
      icon: (ws.icon as string | null | undefined) ?? null,
      color: (ws.color as string | null | undefined) ?? null,
      // Legacy compatibility
      clientName: ws.name as string,
      clientEmail: '',
      postsThisMonth: (ws.postCount as number) || 0,
      scheduledPosts: 0,
      lastActivity: ws.lastActivityAt as string | undefined,
    };
  });
};

/**
 * Create a new workspace.
 *
 * Only `name` is required. `clientName` and `clientEmail` are optional — if
 * `clientName` is provided, `clientEmail` must also be provided (backend rule).
 */
export const createWorkspace = async (data: {
  name: string;
  clientName?: string;
  clientEmail?: string;
  ownerUserId?: number | null;
  lifecycleStage?: 'ONBOARDING' | 'ACTIVE' | 'CHURNING' | 'PAUSED' | 'GRADUATED';
  brandVoice?: string;
  icon?: string | null;
  color?: string | null;
}): Promise<Workspace> => {
  const response = await apiClient.post('/api/agency/workspaces', data);
  return response.data.data.workspace;
};

/**
 * Delete a workspace
 */
export const deleteWorkspace = async (workspaceId: number): Promise<void> => {
  await apiClient.delete(`/api/agency/workspaces/${workspaceId}`);
};

/**
 * Enter God Mode (impersonate a client workspace)
 */
export const enterGodMode = async (workspaceId: number): Promise<GodModeResponse> => {
  // Flush the old identity's queued tracking events (with its own token) and start a new epoch BEFORE the context swap.
  await resetTracking();
  const response = await apiClient.post(`/api/auth/enter-god-mode/${workspaceId}`);
  broadcastAuthChanged('god-mode-enter');
  return response.data.data;
};

/**
 * Exit God Mode (return to agency view)
 */
export const exitGodMode = async (): Promise<void> => {
  await resetTracking(); // old (God Mode) identity flushed before the context swap
  await apiClient.post('/api/auth/exit-god-mode');
  broadcastAuthChanged('god-mode-exit');
};

/**
 * True when the exit failed only because there is no active God Mode session
 * (backend 400 "No active God Mode session"), i.e. it was already exited
 * elsewhere (another tab, expired session, token refresh).
 */
export const isNoActiveGodModeSessionError = (error: unknown): boolean => {
  const apiError = formatApiError(error);
  return apiError.statusCode === 400 && /no active god mode session/i.test(apiError.message);
};

/**
 * Exit God Mode without ever trapping the caller. "Already exited" is treated
 * as success. Returns 'exited' | 'already-exited'; rethrows genuine failures
 * (network, 5xx) so the caller can report them - callers must still clear
 * state and leave to the portfolio.
 */
export const exitGodModeSafely = async (): Promise<'exited' | 'already-exited'> => {
  try {
    await exitGodMode();
    return 'exited';
  } catch (error) {
    if (isNoActiveGodModeSessionError(error)) {
      broadcastAuthChanged('god-mode-exit');
      return 'already-exited';
    }
    throw error;
  }
};

/**
 * Get agency team members
 */
export const getTeamMembers = async (): Promise<TeamMember[]> => {
  const response = await apiClient.get('/api/agency/team');
  return response.data.data.teamMembers;
};

/**
 * Invite a team member
 */
export const inviteTeamMember = async (data: InviteTeamMemberRequest): Promise<TeamMember> => {
  const response = await apiClient.post('/api/agency/team/invite', data);
  return response.data.data.teamMember;
};

/**
 * Remove a team member
 */
export const removeTeamMember = async (userId: number): Promise<void> => {
  await apiClient.delete(`/api/agency/team/${userId}`);
};

/**
 * Get agency settings
 */
export const getAgencySettings = async (): Promise<AgencySettings> => {
  const response = await apiClient.get('/api/agency/settings');
  return response.data.data.settings;
};

/**
 * Input for updating agency settings
 * Uses backend field names (name, email) not frontend display names (agencyName, agencyEmail)
 */
interface UpdateAgencySettingsInput {
  name?: string;
  email?: string;
}

/**
 * Update agency settings
 */
export const updateAgencySettings = async (
  data: UpdateAgencySettingsInput
): Promise<AgencySettings> => {
  const response = await apiClient.patch('/api/agency/settings', data);
  return response.data.data.settings;
};

/**
 * Update workspace (brand space) settings
 */
export const updateWorkspace = async (
  workspaceId: number,
  data: {
    name?: string;
    logo?: string | null;
    icon?: string | null;
    color?: string | null;
    ownerUserId?: number | null;
    brandVoice?: string | null;
    satisfactionSignal?: 'STRONG' | 'NEUTRAL' | 'TENSE' | null;
  }
): Promise<void> => {
  await apiClient.put(`/api/agency/workspaces/${workspaceId}`, data);
};

/**
 * Get workspace active status
 */
export const getWorkspaceStatus = async (
  workspaceId: number
): Promise<{ isActive: boolean }> => {
  const response = await apiClient.get(`/api/agency/workspaces/${workspaceId}/status`);
  return response.data.data;
};

/**
 * Update workspace active status
 */
export const updateWorkspaceStatus = async (
  workspaceId: number,
  isActive: boolean
): Promise<void> => {
  await apiClient.patch(`/api/agency/workspaces/${workspaceId}/status`, { isActive });
};

/**
 * Get workspace carte blanche setting
 */
export const getWorkspaceCarteBlanche = async (
  workspaceId: number
): Promise<{ carteBlanche: boolean }> => {
  const response = await apiClient.get(`/api/agency/workspaces/${workspaceId}/carte-blanche`);
  return response.data.data;
};

/**
 * Update workspace carte blanche setting
 */
export const updateWorkspaceCarteBlanche = async (
  workspaceId: number,
  carteBlanche: boolean
): Promise<void> => {
  await apiClient.patch(`/api/agency/workspaces/${workspaceId}/carte-blanche`, { carteBlanche });
};

/**
 * Upload workspace logo
 */
export const uploadWorkspaceLogo = async (
  workspaceId: number,
  file: File
): Promise<{ logoUrl: string }> => {
  const formData = new FormData();
  formData.append('logo', file);

  const response = await apiClient.post(
    `/api/agency/workspaces/${workspaceId}/logo`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data.data;
};

/**
 * Get team member's client access information
 */
export const getTeamMemberClientAccess = async (userId: number): Promise<ClientAccessInfo> => {
  const response = await apiClient.get(`/api/agency/team/${userId}/clients`);
  return response.data.data;
};

/**
 * Assign client access to a team member
 */
export const assignClientAccess = async (userId: number, teamIds: number[]): Promise<void> => {
  await apiClient.post(`/api/agency/team/${userId}/clients`, { teamIds });
};

/**
 * Revoke client access from a team member
 */
export const revokeClientAccess = async (userId: number, teamId: number): Promise<void> => {
  await apiClient.delete(`/api/agency/team/${userId}/clients/${teamId}`);
};

/**
 * Grant or revoke a team member's agency-wide reviewer flag
 * (agency-admin only). Applies to all of the agency's brand spaces.
 */
export const updateStaffReviewer = async (
  userId: number,
  isReviewer: boolean
): Promise<{ isReviewer: boolean }> => {
  const response = await apiClient.patch(`/api/agency/team/${userId}/reviewer`, { isReviewer });
  return response.data.data;
};

/**
 * Switch a MANAGER between all brands and assigned brands only (owner/admin only)
 */
export const updateStaffBrandScope = async (
  userId: number,
  seesAllBrands: boolean
): Promise<{ seesAllBrands: boolean }> => {
  const response = await apiClient.patch(`/api/agency/team/${userId}/brand-scope`, { seesAllBrands });
  return response.data.data;
};

/**
 * Deep search workspaces by query string
 * Returns workspaces matching name, client name, posts, or member content
 */
export const searchWorkspaces = async (query: string): Promise<WorkspaceSearchResult[]> => {
  const response = await apiClient.get('/api/agency/workspaces/search', {
    params: { q: query },
  });
  return response.data.data;
};

export const agencyService = {
  getWorkspaces,
  createWorkspace,
  deleteWorkspace,
  enterGodMode,
  exitGodMode,
  getTeamMembers,
  inviteTeamMember,
  removeTeamMember,
  getAgencySettings,
  updateAgencySettings,
  updateWorkspace,
  getWorkspaceStatus,
  updateWorkspaceStatus,
  getWorkspaceCarteBlanche,
  updateWorkspaceCarteBlanche,
  uploadWorkspaceLogo,
  getTeamMemberClientAccess,
  assignClientAccess,
  revokeClientAccess,
  updateStaffReviewer,
  updateStaffBrandScope,
  searchWorkspaces,
};
